import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetApiCompaniesCurrentEmployeesReportingTotalpoints } from '@/api/generated/company-management-user/company-management-user'
import { ChartCard } from '../components/ChartCard'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Rectangle, Legend } from 'recharts'
import { ChartTooltip } from '../components/ChartTooltip'
import { FilterRowCharts } from '@/components/common/filters/FilterRowCharts'
import { FilterRowChartItem } from '@/components/common/filters/FilterRowChartItem'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

// Colors for this chart only — intentionally local, so that changes here do not affect other charts
const COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)']
const COUNT_OPTIONS = [5, 10, 15, 20]

export function TotalPointsChart() {
  const { t } = useTranslation('dashboard')
  const [count, setCount] = useState(5)

  const { data, isFetching, isError } = useGetApiCompaniesCurrentEmployeesReportingTotalpoints({ take: count })

  const chartData = (data?.data ?? []).map((item, index) => ({
      name: [item.firstName, item.lastName].filter(Boolean).join(' ') || '—',
      value: item.totalPoints,
      fill: COLORS[index % COLORS.length],
    }))
    .sort((first, second) => second.value - first.value)

  function chart() {
    return (
      <LoaderGeneral isLoading={isFetching} isError={isError} isEmpty={chartData.length === 0} className="h-[260px]">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
            <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: 'var(--foreground)' }} tickLine={false} axisLine={false} />
            <Tooltip content={<ChartTooltip valueLabel={t('charts.totalPoints.tooltipLabel')} />} cursor={{ fill: 'var(--muted)' }} />
            <Legend formatter={() => t('charts.totalPoints.legend')} />
            <Bar
              dataKey="value"
              radius={[0, 4, 4, 0]}
              maxBarSize={22}
              shape={(props: any) => <Rectangle {...props} fill={props.payload.fill} />}
            />
          </BarChart>
        </ResponsiveContainer>
      </LoaderGeneral>
    )
  }

  function controls() {
    return (
      <FilterRowCharts>
        <FilterRowChartItem label={t('charts.filters.count')}>
          <Select value={String(count)} onValueChange={(v) => setCount(Number(v))}>
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNT_OPTIONS.map((number) => <SelectItem key={number} value={String(number)}>{number}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterRowChartItem>
      </FilterRowCharts>
    )
  }

  return (
    <ChartCard
      title={t('charts.totalPoints.title')}
      chart={chart()}
      controls={controls()}
    />
  )
}
